import { createSelector } from 'reselect';
import {
    transformTags,
    transformSelectDate,
    transformContributors,
} from '../Dashboard/transformers';
import {
    transformChallenge,
    transformConnectedChallenges,
} from '../Challenges/transformer';
import { transformMapData } from '../../map/transformers';
import {
    transformSegments,
    transformNotifications,
    transformChallengeCodes,
} from './transformers';
import { numberWithCommas } from '../../common/utils';
import { NOTIFICATION_LIMIT } from './constants';

export const challengeIdState = (state) => state.challenge.challengeId || null;

export const challengeState = (state) => state.challenge.challenge || {};

export const challengeLoadingState = (state) =>
    state.challenge.challengeRequested || null;

export const connectedChallengesState = (state) =>
    state.challenge.connectedChallenges || [];

export const parentChallengeState = (state) =>
    state.challenge.parentChallenge || null;

export const connectedChallengesLoadingState = (state) =>
    state.challenge.connectedChallengesLoading || [];

export const getChallengeCodes = (state) =>
    state.challenge.challengeCodes || [];

export const getChallengeCodesForTopContributorsState = (state) =>
    state.challenge.challengeCodes;
export const getChallengeCodesForTopContributors = createSelector(
    getChallengeCodesForTopContributorsState,
    (challengeCodes) => transformChallengeCodes(challengeCodes || []),
);

export const getChallengeCodesLoading = (state) =>
    state.challenge.challengeCodesLoading;

export const getChallengeCodeLoading = (state) => state.challenge.addingNewCode;

export const getChallengeError = (state) => state.challenge.error;

export const getChallengeCodeUpdating = (state) => state.challenge.updatingCode;

export const getChallengeUpdateError = (state) => state.challenge.updatingError;

export const getChallengeCodeError = (state) => state.challenge.codeAddingError;

export const segmentIdState = (state) => state.challenge.segmentId || null;

export const getTaxonomyState = (state) => state.challenge.taxonomy || '';

export const getSegmentTaxonomyState = (state) =>
    state.challenge.segmentTaxonomy || '';

export const getSelectDateState = (state) => state.challenge.date || '';

export const getCustomDateState = (state) => state.challenge.customDate || {};

export const getTopTagsState = (state) =>
    state.challenge.topTags || { mlTags: [], userTags: [] };

export const getChallengeSegmentTags = (state) => state.challenge.segmentTags;

export const getSegmentTagsState = createSelector(
    getChallengeSegmentTags,
    (tagsState) => tagsState.topTags || { mlTags: [], userTags: [] },
);

export const getTopContributorsState = (state) =>
    state.challenge.topContributors || [];

export const getContributorsLoadingState = (state) =>
    state.challenge.contributorsLoading || false;

export const getSegmentTagsLoadingState = (state) =>
    state.challenge.segmentTagsLoading || false;

export const getSegmentsLoadingState = (state) =>
    state.challenge.segmentsLoading || false;

export const getTagsLoadingState = (state) =>
    state.challenge.tagsLoading || false;

export const getTotalTagsState = (state) => state.challenge.totalTags || 0;

export const getSegmentTagsTotalState = createSelector(
    getChallengeSegmentTags,
    (tagsState) => tagsState.totalTags || 0,
);

export const getSegmentsState = (state) => state.challenge.segments || [];

export const getCodeId = (state) => state.challenge.codeId;

export const getTopContributors = createSelector(
    getTopContributorsState,
    (topContributors) => transformContributors(topContributors),
);

export const getContributorsLoading = createSelector(
    getContributorsLoadingState,
    (loading) => loading,
);

export const getChallengeId = createSelector(
    challengeIdState,
    (challengeId) => challengeId,
);

export const getChallenge = createSelector(challengeState, (challenge) =>
    transformChallenge(challenge),
);

export const getChallengeOriginal = createSelector(
    challengeState,
    (challenge) => challenge,
);

export const getChallengeLoading = createSelector(
    challengeLoadingState,
    (loading) => loading,
);

export const getConnectedChallenges = createSelector(
    connectedChallengesState,
    (challenges) => transformConnectedChallenges(challenges),
);

export const getParentChallenge = createSelector(
    parentChallengeState,
    (challenge) => challenge,
);

export const getConnectedChallengesLoading = createSelector(
    connectedChallengesLoadingState,
    (loading) => loading,
);

export const getSegmentId = createSelector(
    segmentIdState,
    (segmentId) => segmentId,
);

export const getSegmentTaxonomy = createSelector(
    getSegmentTaxonomyState,
    (taxonomy) => taxonomy,
);

export const getTaxonomy = createSelector(
    getTaxonomyState,
    (taxonomy) => taxonomy,
);

export const getTopTags = createSelector(getTopTagsState, (topTags) =>
    transformTags(topTags.userTags, 'orange'),
);

export const getMlTags = createSelector(getTopTagsState, (topTags) =>
    transformTags(topTags.mlTags, 'blue'),
);

export const getSegmentTags = createSelector(getSegmentTagsState, (tags) =>
    transformTags(tags.userTags, 'blue'),
);

export const getSegmentMLTags = createSelector(getSegmentTagsState, (tags) =>
    transformTags(tags.mlTags, 'orange'),
);

export const getSegments = createSelector(getSegmentsState, (segments) =>
    transformSegments(segments),
);

export const getTagsLoading = createSelector(
    getTagsLoadingState,
    (loading) => loading,
);

export const getSegmentTagsLoading = createSelector(
    getSegmentTagsLoadingState,
    (loading) => loading,
);

export const getSegmentsLoading = createSelector(
    getSegmentsLoadingState,
    (loading) => loading,
);

export const getTotalTags = createSelector(getTotalTagsState, (totalTags) =>
    numberWithCommas(totalTags),
);

export const getSegmentTotalTags = createSelector(
    getSegmentTagsTotalState,
    (totalTags) => numberWithCommas(totalTags),
);

export const getSelectDate = createSelector(
    getSelectDateState,
    getCustomDateState,
    (date, customDate) => transformSelectDate(date, customDate),
);

// map state

const filterState = (state) => state.challenge.selectedFilter || '';
const mapData = (state) => state.challenge.impactLitters || [];
const litterRequested = (state) => state.challenge.litterRequested || false;

export const getSelectedFilter = createSelector(
    filterState,
    (filterState) => filterState,
);

export const isMapFilterRequested = createSelector(
    litterRequested,
    (litterRequested) => litterRequested,
);

export const getImpactLitters = createSelector(
    [mapData, filterState],
    (mapData, filterState) => transformMapData(mapData, filterState),
);

export const getRequestedSegmentsLimit = (state) =>
    state.challenge.segmentsLimit || false;

export const getChallengeTotalMlTags = (state) =>
    state.challenge.totalMlTags || '';

export const getChallengeTotalDistilledTags = (state) =>
    state.challenge.totalDistilledTags || '';

export const getChallengeNotifications = (state) =>
    transformNotifications(state.challenge.notifications) || [];

export const getChallengeNotificationsLoading = (state) =>
    state.challenge.challengeNotificationsLoading;

export const getAddNotificationLoading = (state) =>
    state.challenge.addingNotification;

export const getNotificationCurrentPageState = (state) =>
    state.challenge.notificationCurrentPage || 0;

export const getNotificationCurrentPage = createSelector(
    getNotificationCurrentPageState,
    (currentPage) => NOTIFICATION_LIMIT * currentPage,
);
export const getNotificationMoreData = createSelector(
    (state) => state.challenge.hasMoreNotifications,
    (hasMoreNotifications) => hasMoreNotifications,
);
